import { Head, Link, usePage } from "@inertiajs/react"
import { AlertCircle, FileWarning, Inbox, Upload, Users } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Badge } from "@/components/ui/badge"
import { countdown } from "@/lib/accountDirectives"
import { MONTHLY_CATEGORIES, type MonthlyCategoryKey } from "@/lib/datasetRegistry"
import type { PageProps } from "@/types/inertia"

type Stats = {
  total_users: number
  admin_users: number
  new_users_this_week: number
  new_users_this_month: number
  inbox_unread: number
  inbox_total: number
}

type UserRow = {
  id: number
  email: string
  admin: boolean
  created_at: string
}

type Props = {
  stats: Stats
  recent_users: UserRow[]
}

// Labels + hrefs derive from the single monthly-upload source in
// datasetRegistry.ts (this also gives niq_fragrances a proper card, which the
// old hand-written switch silently dropped to the default branch).
function getCategoryInfo(key: string) {
  const cat = MONTHLY_CATEGORIES[key as MonthlyCategoryKey]
  return cat ? { label: cat.label, href: cat.uploadsHref } : { label: key, href: "/admin" }
}

export default function AdminDashboard({ stats, recent_users }: Props) {
  const { props } = usePage<PageProps>()
  const monthlyStatus = props.monthly_upload_status
  const incompleteUploads = monthlyStatus
    ? Object.entries(monthlyStatus).filter(([_, status]) => !status.completed)
    : []
  const deadlineAlerts = props.directive_deadline_alerts ?? []

  const currentMonthYear = new Date().toLocaleDateString("id-ID", {
    month: "long",
    year: "numeric"
  })

  return (
    <>
      <Head title="Dashboard">
        <meta name="description" content="Admin dashboard overview." />
        <meta property="og:title" content="Dashboard" />
        <meta property="og:description" content="Admin dashboard overview." />
      </Head>
      <AppShell>
        <div className="border-b border-hairline pb-6">
          <h1>Dashboard</h1>
          <p className="mt-1">Overview of your app.</p>
        </div>

        {incompleteUploads.length > 0 && (
          <div className="mt-6 rounded-lg border border-warning/20 bg-warning-faded p-5">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 shrink-0 text-warning-display" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-warning-display">
                  Beberapa Data Bulanan Belum Lengkap
                </h3>
                <p className="mt-1 text-xs text-ink-muted leading-relaxed">
                  Data untuk bulan berjalan ({currentMonthYear}) belum sepenuhnya di-upload.
                </p>

                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {incompleteUploads.map(([key, status]) => {
                    const info = getCategoryInfo(key)
                    return (
                      <div key={key} className="flex items-center justify-between rounded-md border border-hairline bg-page p-3 shadow-sm">
                        <div className="min-w-0 flex-1 pr-2">
                          <div className="text-xs font-semibold text-ink-display truncate">{info.label}</div>
                          <div className="mt-0.5 text-[10px] text-ink-muted leading-normal">
                            {status.latest_period ? (
                              <span>
                                Sistem saat ini menggunakan data bulan <strong>{status.latest_period}</strong> sebagai acuan karena data bulan <strong>{currentMonthYear}</strong> belum di-upload.
                              </span>
                            ) : (
                              <span>
                                Data bulan <strong>{currentMonthYear}</strong> belum di-upload dan tidak ada data acuan sebelumnya.
                              </span>
                            )}
                            {status.uploaded_count !== undefined && (
                              <span className="block mt-0.5 text-accent font-medium">
                                Progress: {status.uploaded_count}/{status.total_count} distributor/akun
                              </span>
                            )}
                          </div>
                        </div>
                        <Link
                          href={info.href}
                          className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-surface text-ink-muted hover:bg-warning-faded hover:text-warning-display transition-colors"
                          title={`Upload ${info.label}`}
                        >
                          <Upload className="h-3.5 w-3.5" />
                        </Link>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {deadlineAlerts.length > 0 && (
          <div className="mt-6 rounded-lg border border-danger/20 bg-danger-faded p-5">
            <div className="flex items-start gap-3">
              <FileWarning className="w-5 h-5 shrink-0 text-danger-display" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-danger-display">
                  Peringatan Produk Mendekati / Melewati Tenggat
                </h3>
                <p className="mt-1 text-xs text-ink-muted leading-relaxed">
                  {deadlineAlerts.length} peringatan aktif memiliki masa tenggang (grace) dalam 30 hari ke depan atau telah terlewati.
                </p>

                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {deadlineAlerts.map((alert) => {
                    const c = countdown(alert.grace_deadline)
                    return (
                      <Link
                        key={alert.id}
                        href={`/admin/accounts/directives/${alert.id}`}
                        className="flex items-center justify-between rounded-md border border-hairline bg-page p-3 shadow-sm transition-colors hover:border-danger/40"
                      >
                        <div className="min-w-0 flex-1 pr-2">
                          <div className="text-xs font-semibold text-ink-display truncate">
                            {alert.account_name ?? "—"}
                          </div>
                          <div className="mt-0.5 text-[10px] text-ink-muted leading-normal truncate">
                            {alert.ref_no ?? "Tanpa nomor"}
                            {alert.brand ? ` · ${alert.brand}` : ""} · grace {formatDate(alert.grace_deadline)}
                          </div>
                        </div>
                        {c && (
                          <Badge tone={c.tone} className="shrink-0">
                            {c.label}
                          </Badge>
                        )}
                      </Link>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3">
          <StatCard
            label="Total users"
            value={stats.total_users}
            href="/admin/users"
            icon={<Users className="h-4 w-4" />}
          />
          <StatCard
            label="New this week"
            value={stats.new_users_this_week}
            href="/admin/users"
            icon={<Users className="h-4 w-4" />}
          />
          <StatCard
            label="New this month"
            value={stats.new_users_this_month}
            href="/admin/users"
            icon={<Users className="h-4 w-4" />}
          />
          <StatCard
            label="Unread emails"
            value={stats.inbox_unread}
            href="/admin/inbox"
            icon={<Inbox className="h-4 w-4" />}
          />
          <StatCard
            label="Total emails"
            value={stats.inbox_total}
            href="/admin/inbox"
            icon={<Inbox className="h-4 w-4" />}
          />
          <StatCard
            label="Admin users"
            value={stats.admin_users}
            href="/admin/users"
            icon={<Users className="h-4 w-4" />}
          />
        </div>

        <div className="mt-8">
          <div className="flex items-center justify-between">
            <h2>Recent users</h2>
            <Link href="/admin/users" className="text-sm text-ink-muted no-underline hover:text-ink-display">
              View all
            </Link>
          </div>
          <ul className="mt-3 divide-y divide-hairline overflow-hidden rounded-md border border-hairline bg-page">
            {recent_users.map((user) => (
              <li key={user.id}>
                <Link
                  href={`/admin/users/${user.id}`}
                  className="flex items-center justify-between px-4 py-3 no-underline hover:bg-surface"
                >
                  <div>
                    <div className="text-sm font-medium text-ink-display">{user.email}</div>
                    <div className="text-xs text-ink-muted">Joined {formatDate(user.created_at)}</div>
                  </div>
                  {user.admin && (
                    <span className="rounded-full bg-accent/10 px-2 py-0.5 text-xs font-medium text-accent">
                      Admin
                    </span>
                  )}
                </Link>
              </li>
            ))}
            {recent_users.length === 0 && (
              <li className="px-4 py-6 text-center text-sm text-ink-muted">No users yet.</li>
            )}
          </ul>
        </div>
      </AppShell>
    </>
  )
}

function StatCard({
  label,
  value,
  href,
  icon,
}: {
  label: string
  value: number
  href: string
  icon: React.ReactNode
}) {
  return (
    <Link
      href={href}
      className="flex flex-col gap-2 rounded-md border border-hairline bg-surface p-4 no-underline hover:bg-surface/80"
    >
      <div className="flex items-center justify-between text-ink-muted">
        <span className="text-xs">{label}</span>
        {icon}
      </div>
      <span className="text-2xl font-semibold text-ink-display">{value}</span>
    </Link>
  )
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}
